import { HttpClient, HttpEventType, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  take,
  throwError,
} from 'rxjs';
import { environment } from '../../environment/environment';
import { EditAssetDialogResult } from '../components/shared-components/edit-asset-dialog/edit-asset-dialog.component';
import {
  FileBackendModel,
  FileDetailsBackendModel,
  FileModel,
} from '../models/file.model';
import { TrainingCategoryService } from './training-category.service';

interface UploadFileBackendResponse {
  success: boolean;
  message: string;
  data: FileBackendModel;
}

interface UploadFileResponse {
  success: boolean;
  message: string;
  data: FileModel;
}

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private loadingFileSubject = new BehaviorSubject<number | null>(null);
  private readonly previewObjectUrlLifetimeMs = 10 * 60 * 1000; // 10 minutes
  private readonly objectUrlTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  get loadingFile$(): Observable<number | null> {
    return this.loadingFileSubject.asObservable();
  }

  constructor(
    private http: HttpClient,
    private courseTypeService: TrainingCategoryService
  ) {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.revokeAllObjectUrls);
    }
  }

  uploadFile(
    file: File,
    tagIds: number[],
    seminarIds: number[] = []
  ): Observable<{ progress: number; response?: UploadFileResponse }> {
    const formData = new FormData();
    formData.append('mode', 'uploadFile');
    formData.append('file', file);
    formData.append('tag_ids', JSON.stringify(tagIds));
    formData.append('seminar_ids', JSON.stringify(seminarIds));

    // Create an HttpRequest with reportProgress enabled
    const req = new HttpRequest(
      'POST',
      `${environment.authority_short}file.php`,
      formData,
      {
        reportProgress: true,
      }
    );

    return this.http.request(req).pipe(
      map(event => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const progress = event.total
              ? Math.round((100 * event.loaded) / event.total)
              : 0;
            return { progress };
          case HttpEventType.Response:
            const response = event.body as UploadFileBackendResponse;
            return {
              progress: 100,
              response: {
                success: response.success,
                message: response.message,
                data: this.parseBackendFile(response.data as FileBackendModel),
              },
            };
          default:
            return { progress: 0 };
        }
      }),
      catchError(error => throwError(() => error))
    );
  }

  getFilesBySeminar(id: number): Observable<FileModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getFilesBySeminar');
    formData.append('id_seminar', id.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: FileBackendModel[];
      }>(`${environment.authority_short}file.php`, formData)
      .pipe(
        map(
          response =>
            response.data?.map((file: FileBackendModel) =>
              this.parseBackendFile(file)
            ) ?? []
        ),
        catchError(() =>
          throwError(() => new Error('Error getting files by seminar'))
        )
      );
  }

  getFilesByUser(): Observable<FileModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getFilesByUser');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: FileBackendModel[];
      }>(`${environment.authority_short}file.php`, formData)
      .pipe(
        map(
          response =>
            response.data?.map((file: FileBackendModel) =>
              this.parseBackendFile(file)
            ) ?? []
        ),
        catchError(() =>
          throwError(() => new Error('Error getting files by user'))
        )
      );
  }

  getFile(id: number): Observable<Blob> {
    const formData = new FormData();
    formData.append('mode', 'getFile');
    formData.append('id', id.toString());

    return this.http
      .post(`${environment.authority_short}file.php`, formData, {
        responseType: 'blob',
      })
      .pipe(
        map(response => response as Blob),
        catchError(() => throwError(() => new Error('Error getting file')))
      );
  }

  deleteFile(id: number): Observable<boolean> {
    this.loadingFileSubject.next(id);

    const formData = new FormData();
    formData.append('mode', 'deleteFile');
    formData.append('id_file', id.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(`${environment.authority_short}file.php`, formData)
      .pipe(
        map(response => {
          this.loadingFileSubject.next(null);
          return response.success;
        }),
        catchError(() => {
          this.loadingFileSubject.next(null);
          return throwError(() => new Error('Error deleting file'));
        })
      );
  }

  detachFileFromSeminar(id: number, seminarId: number): Observable<boolean> {
    this.loadingFileSubject.next(id);
    const formData = new FormData();
    formData.append('mode', 'deleteFile');
    formData.append('id_file', id.toString());
    formData.append('id_seminar', seminarId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(`${environment.authority_short}file.php`, formData)
      .pipe(
        map(response => {
          this.loadingFileSubject.next(null);
          return response.success;
        }),
        catchError(() => {
          this.loadingFileSubject.next(null);
          return throwError(() => new Error('Error detaching file'));
        })
      );
  }

  renameFile(fileId: number, newName: string): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'renameFile');
    formData.append('id_file', fileId.toString());
    formData.append('new_name', newName);

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(`${environment.authority_short}file.php`, formData)
      .pipe(
        map(response => {
          if (!response.success) {
            throw new Error(response.message);
          }
        }),
        catchError(error =>
          throwError(() => new Error(error?.message || 'Failed to rename file'))
        )
      );
  }

  openFile(file: FileModel): void {
    this.loadingFileSubject.next(file.id);

    this.getFile(file.id)
      .pipe(take(1))
      .subscribe({
        next: blob => {
          const url = this.createManagedObjectUrl(blob);
          window.open(url, '_blank', 'noopener');
          this.loadingFileSubject.next(null);
        },
        error: () => {
          this.loadingFileSubject.next(null);
        },
      });
  }

  downloadFile(file: FileModel): void {
    this.loadingFileSubject.next(file.id);
    this.getFile(file.id)
      .pipe(take(1))
      .subscribe({
        next: blob => {
          const url = window.URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = file.name;
          anchor.click();
          setTimeout(() => this.revokeObjectUrl(url), 1000);
          this.loadingFileSubject.next(null);
        },
        error: () => {
          this.loadingFileSubject.next(null);
        },
      });
  }

  private createManagedObjectUrl(blob: Blob): string {
    const url = window.URL.createObjectURL(blob);
    const existingTimer = this.objectUrlTimers.get(url);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      this.revokeObjectUrl(url);
    }, this.previewObjectUrlLifetimeMs);

    this.objectUrlTimers.set(url, timer);
    return url;
  }

  private revokeObjectUrl(url: string): void {
    const timer = this.objectUrlTimers.get(url);
    if (timer) {
      clearTimeout(timer);
      this.objectUrlTimers.delete(url);
    }
    window.URL.revokeObjectURL(url);
  }

  private revokeAllObjectUrls = (): void => {
    this.objectUrlTimers.forEach((timer, url) => {
      clearTimeout(timer);
      window.URL.revokeObjectURL(url);
    });
    this.objectUrlTimers.clear();
  };

  parseBackendFile(file: FileBackendModel): FileModel {
    return {
      id: file.id,
      name: file.name,
      type: file.type,
      size: file.size,
      tags: file.tags,
      category1:
        file.category1 &&
        this.courseTypeService.parseBackendModel(file.category1),
      category2:
        file.category2 &&
        this.courseTypeService.parseBackendModel(file.category2),
      position: file.position ?? null,
      createdAt: file.createdAt ? new Date(file.createdAt) : null,
    };
  }

  updateFileOrder(
    fileOrder: { id: number; position: number }[],
    seminarId: number
  ): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'reorderSeminarFiles');
    formData.append('id_seminar', seminarId.toString());
    fileOrder.forEach((fo, idx) => {
      formData.append(`id[${idx}]`, fo.id.toString());
      formData.append(`position[${idx}]`, fo.position.toString());
    });
    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'file.php', formData)
      .pipe(
        map(() => undefined),
        catchError(() => throwError(() => new Error('Failed to reorder files')))
      );
  }

  getFilesBySeminars(seminarIds: number[]): Observable<FileModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getFilesBySeminars');
    formData.append('seminar_ids', JSON.stringify(seminarIds));

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: FileBackendModel[];
      }>(`${environment.authority_short}file.php`, formData)
      .pipe(
        map(
          response =>
            response.data?.map((file: FileBackendModel) =>
              this.parseBackendFile(file)
            ) ?? []
        ),
        catchError(() =>
          throwError(() => new Error('Error getting files by seminars'))
        )
      );
  }

  getAllFilesAdmin(): Observable<FileModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getAllFilesAdmin');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: FileBackendModel[];
      }>(`${environment.authority_short}file.php`, formData)
      .pipe(
        map(
          response =>
            response.data?.map((file: FileBackendModel) =>
              this.parseBackendFile(file)
            ) ?? []
        ),
        catchError(() => throwError(() => new Error('Error getting all files')))
      );
  }

  getFileDetails(fileId: number): Observable<FileDetailsBackendModel> {
    const formData = new FormData();
    formData.append('mode', 'getFileDetails');
    formData.append('id_file', fileId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: FileDetailsBackendModel;
      }>(`${environment.authority_short}file.php`, formData)
      .pipe(
        map(response => response.data),
        catchError(() =>
          throwError(() => new Error('Error getting file details'))
        )
      );
  }

  updateFileDetails(
    fileId: number,
    payload: EditAssetDialogResult
  ): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'updateFileDetails');
    formData.append('id_file', fileId.toString());
    formData.append('name', payload.name);
    formData.append('tag_ids', JSON.stringify(payload.tagIds));
    formData.append('seminar_ids', JSON.stringify(payload.seminarIds));

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(`${environment.authority_short}file.php`, formData)
      .pipe(
        map(() => undefined),
        catchError(() =>
          throwError(() => new Error('Error updating file details'))
        )
      );
  }
}
